// src/DropdownMenu.js
import React, { useState, useEffect, useRef } from 'react';

/**
 * A generic dropdown menu component.
 * @param {object} props - Component props
 * @param {string} props.triggerText - Text displayed on the button that opens the menu.
 * @param {Array<{key: string, label: string}>} [props.items=[]] - Array of objects for menu items. Each object needs 'key' and 'label'.
 * @param {function(string): void} props.onItemSelect - Callback function executed when a menu item is clicked, passing the item's key.
 * @param {string} [props.buttonClassName='nav-button'] - CSS class(es) for the trigger button.
 * @param {string} [props.menuClassName='dropdown-menu'] - CSS class(es) for the dropdown container div.
 * @param {string} [props.itemClassName='menu-item'] - CSS class(es) for each button/item within the dropdown.
 * @param {string} [props.containerClassName='dropdown-container'] - CSS class(es) for the main wrapper div.
 */
function DropdownMenu({
  triggerText,
  items = [],
  onItemSelect,
  buttonClassName = 'nav-button',
  menuClassName = 'dropdown-menu',
  itemClassName = 'menu-item',
  containerClassName = 'dropdown-container'
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null); // Ref for detecting clicks outside the menu

  // Function to toggle the menu's visibility
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Effect to add/remove event listener for closing menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the menu ref exists and the click was outside of it
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false); // Close the menu
      }
    }
    // Add listener only when the menu is open
    if (isMenuOpen) {
       document.addEventListener("mousedown", handleClickOutside);
    } else {
       // Remove listener if menu is closed (important for performance)
       document.removeEventListener("mousedown", handleClickOutside);
    }
    // Cleanup function: Remove listener when component unmounts or effect re-runs
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]); // Only re-run this effect if isMenuOpen changes

  // Function called when a menu item button is clicked
  const handleSelection = (itemKey) => {
    // Call the callback function passed via props, if it exists
    if (onItemSelect) {
      onItemSelect(itemKey);
    } else {
      // Provide a fallback log if no handler is passed (useful for testing)
      console.log(`DropdownMenu: Selected item key: ${itemKey}`);
    }
    setIsMenuOpen(false); // Close the menu after selection
  };

  return (
    // Use the ref and className prop on the main container div
    <div className={containerClassName} ref={menuRef}>

      {/* Trigger Button - text comes from props */}
      <button onClick={toggleMenu} className={buttonClassName}>
         {triggerText} {isMenuOpen ? '▲' : '▼'} {/* Show appropriate arrow */}
      </button>

      {/* Dropdown Menu - rendered conditionally based on state */}
      {isMenuOpen && (
        <div className={menuClassName}>
          {/* Map over the items array prop to create menu item buttons */}
          {items.map(item => (
            <button
              key={item.key} // Use item's unique key for React's key prop
              onClick={() => handleSelection(item.key)} // Pass item's key to handler on click
              className={itemClassName} // Apply item class for styling
            >
              {item.label} {/* Display the item's label */}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default DropdownMenu;
